import React, { useState, useEffect } from "react";
import { Wallet, BarChart3, Activity, Zap } from "lucide-react";
import { SearchBox } from "../../../../../components/Search";
import {
  getStateCostVolumeSummary,
  StateCostVolumeSummaryResponse,
} from "../../../../../Rest_api/RestApi";

interface StateSummaryTilesProps {
  availableDates: any[];
  selectedState: string;
  selectedExchanges: string[];
  selectedMarkets: string[];
}

const StateSummaryTiles: React.FC<StateSummaryTilesProps> = ({
  availableDates,
  selectedState,
  selectedExchanges,
  selectedMarkets,
}) => {
  const [selectedFilterDate, setSelectedFilterDate] = useState<any[]>([]);
  const [data, setData] = useState<StateCostVolumeSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);

  // 1. Sync selected dates with availableDates (remove stale selections)
  useEffect(() => {
    if (availableDates.length > 0) {
      const validSelectedDates = selectedFilterDate.filter((selected) =>
        availableDates.some((available) => available.value === selected.value),
      );

      if (validSelectedDates.length === 0) {
        // If no valid selection or selection cleared, default to the first available date
        setSelectedFilterDate([availableDates[0]]);
      } else if (validSelectedDates.length !== selectedFilterDate.length) {
        // If some were invalid, update state
        setSelectedFilterDate(validSelectedDates);
      }
    } else {
      // If no available dates, clear selection
      if (selectedFilterDate.length > 0) setSelectedFilterDate([]);
    }
  }, [availableDates]);

  // 2. Fetch data whenever selectedFilterDate (or filters) change
  useEffect(() => {
    const fetchData = async () => {
      if (selectedFilterDate.length === 0 || !selectedState) {
        setData(null);
        return;
      }

      setLoading(true);
      try {
        // Use the FIRST selected date range (since isMany=false)
        const dateOption = selectedFilterDate[0];
        const dateRange = dateOption.value.split(",");
        const startDate = dateRange[0];
        const endDate = dateRange[1] || startDate;

        const result = await getStateCostVolumeSummary({
          startDate,
          endDate,
          state: selectedState,
          product: selectedMarkets.join(","),
          exchange: selectedExchanges.join(","),
        });
        setData(result);
      } catch (error) {
        console.error("Error fetching state summary tiles data", error);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedFilterDate, selectedState, selectedExchanges, selectedMarkets]);

  const formatValue = (val: number | undefined) => {
    if (val === undefined || val === null) return "-";
    return val.toLocaleString("en-IN", { maximumFractionDigits: 2 });
  };

  const tiles = [
    {
      label: "Total Drawal Cost",
      value: data ? `${formatValue(data.total_drawal_cost)} ` : "-",
      icon: <Wallet className="w-4 h-4 text-white" />,
      bgColor: "bg-[#FFF6E9]", // Light Orange
      iconBg: "bg-[#F97316]", // Orange
      unit: "Cr",
    },
    {
      label: "Total Drawal Volume",
      value: data ? `${formatValue(data.total_drawal_volume_mu)}` : "-",
      icon: <BarChart3 className="w-4 h-4 text-white" />,
      bgColor: "bg-[#F3E8FF]", // Light Purple
      iconBg: "bg-[#A855F7]", // Purple
      unit: "MU",
    },
    {
      label: "Total Injection Cost",
      value: data ? `${formatValue(data.total_injection_cost)}` : "-",
      icon: <Wallet className="w-4 h-4 text-white" />,
      bgColor: "bg-[#ECFDF5]", // Light Green
      iconBg: "bg-[#22C55E]", // Green
      unit: "Cr",
    },
    {
      label: "Total Injection Volume",
      value: data ? `${formatValue(data.total_injection_volume_mu)}` : "-",
      icon: <BarChart3 className="w-4 h-4 text-white" />,
      bgColor: "bg-[#EFF6FF]", // Light Blue
      iconBg: "bg-[#3B82F6]", // Blue
      unit: "MU",
    },
  ];

  return (
    <div className="flex flex-row gap-2 w-full mb-2 items-stretch">
      {tiles.map((tile, index) => (
        <div
          key={index}
          className={`${tile.bgColor} flex-1 rounded-xl px-2 py-1.5 flex items-center justify-between border border-gray-200 hover:border-gray-200 transition-all shadow-sm`}
        >
          <div className="flex items-center gap-2">
            <div
              className={`${tile.iconBg} p-1 rounded-lg shadow-sm flex items-center justify-center`}
            >
              {tile.icon}
            </div>
            <div className="flex flex-col flex-1 min-w-0 justify-left">
              <span className="text-xs font-medium text-gray-500 tracking-normal leading-tight truncate">
                {tile.label}
              </span>
              <div className="flex flex-row items-baseline gap-2 mt-0.5">
                {loading && index === 0 && !data ? (
                  <div className="w-16 bg-gray-200/50 animate-pulse rounded mt-1"></div>
                ) : (
                  <span className="text-xs font-semibold text-[#525062] leading-tight">
                    {tile.value}{" "}
                    <span className="text-[8px] font-semibold text-[#525062] leading-tight">
                      {tile.unit}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}

      <div className="w-[200px] bg-slate-50/50 rounded-lg border border-gray-200/60 p-1 flex flex-col justify-center gap-0.5">
        <span className="text-[10px] font-medium text-[#525062]  tracking-wider px-1">
          Select Date
        </span>
        <div className="[&_div]:!text-[10px] [&>div>div]:!min-h-[22px] [&>div>div]:!py-0 [&>div>div]:!border-0 [&>div>div]:!bg-white [&>div>div]:!shadow-sm text-gray-700">
          <SearchBox
            options={availableDates}
            isMany={false}
            placeholder="Dates"
            selectedOptions={selectedFilterDate}
            onChange={(val: any) => {
              if (val) {
                setSelectedFilterDate(Array.isArray(val) ? val : [val]);
              } else {
                setSelectedFilterDate([]);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default StateSummaryTiles;
